import Languages from '@shared/types/enums/languages';
import PlayerUtils from '@shared/utils/player';
import { IPlayerError } from '@shared/types/interfaces/error';
import { ITrack } from '@shared/types/interfaces/player';
import { PlayerManagerProps as Props } from '@shared/types/props/player';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useCallback, useEffect, useRef } from 'react';
import {
  playerEndAction,
  updateNextEpisode,
} from '@shared/store/thunks/player.thunk';
import {
  PlayerError,
  PlayerState,
  PlayerType,
} from '@shared/types/enums/player';
import {
  mediaSelector,
  setCurrentTime,
  setDuration,
  setError,
  setPosition,
  setRange,
  setReady,
  setState,
  setTracks,
} from '@shared/store/slices/media.slice';

/**
 * ListenerManager component for HLS.js Player.
 * Manages all event listeners for the HLS.js Player instance, including playback events,
 * error handling, level switches, track changes, and analytics tracking.
 * Updates the Redux store with player state, position, duration, and track information.
 *
 * @param {Props} props - Component props containing HLS.js player instance, video element, and playback parameters
 * @returns {null} This component doesn't render anything
 */
export default function ListenerManager({
  hls: player,
  video,
  bookmark,
  start,
  end,
}: Props) {
  const dispatch = useAppDispatch();
  const { audio, live, state, subtitles, userInteraction } =
    useAppSelector(mediaSelector);
  // Track if playback has ended to prevent multiple end events
  const endedRef = useRef(false);
  // Keep user interaction state in ref for event handlers
  const userInteractionRef = useRef(userInteraction);
  userInteractionRef.current = userInteraction;

  // Updaters

  /**
   * Updates playback data including duration, position, and seek range.
   * Calculates position differently for live vs VOD content using video element's seekable range.
   * Dispatches updates to Redux store for current time, duration, position, and range.
   */
  const updateData = useCallback(() => {
    const range = video?.seekable;

    // Calculate time data
    const duration =
      live && range?.length
        ? (range?.end(0) || 0) - (range?.start(0) || 0)
        : end || video?.duration || 0;

    const position = live
      ? Date.now() -
        Math.max(
          (((range?.length ? range?.end(0) : 0) || 0) -
            (video?.currentTime || 0)) *
            1000,
          0
        )
      : Math.max(video?.currentTime || 0, bookmark || 0);

    const seek = live
      ? {
          start: Date.now() - (duration || 0) * 1000,
          end: Date.now(),
        }
      : { start: start || 0, end: duration };

    // If the end is surpassed, finish playback
    if (!endedRef.current && end && position > duration) {
      dispatch(playerEndAction());
      endedRef.current = true;
    }

    // Update media slice
    dispatch(setRange({ seek }));
    dispatch(setCurrentTime(video?.currentTime || 0));
    dispatch(setDuration(duration));
    dispatch(setPosition({ position, seek: false })); // Set position last is best
    dispatch(updateNextEpisode());
  }, [player, video, live]);

  /**
   * Updates the audio track selection.
   * Finds and selects the audio track matching the selected audio ID.
   */
  const updateAudio = useCallback(() => {
    if (player) {
      const track = player?.audioTracks.find(
        (item) => item.id.toString() === audio.id
      );
      player.audioTrack = track?.id ?? player?.audioTrack;
    }
  }, [audio, player]);

  /**
   * Updates subtitle track selection and visibility.
   * Enables/disables subtitles based on track selection.
   */
  const updateSubtitles = useCallback(() => {
    if (player) {
      const track = player?.subtitleTracks.find(
        (item) => item.id.toString() === subtitles.id
      );
      if (track) {
        player.subtitleDisplay = true;
        player.subtitleTrack = track?.id ?? player?.subtitleTrack;
      } else {
        player.subtitleDisplay = false;
      }
    }
  }, [subtitles, player]);

  /**
   * Updates available audio and subtitle tracks from the player.
   * Maps HLS.js track information to the application's track format.
   */
  const updateTracks = useCallback(() => {
    const audio =
      player?.audioTracks.map((item) => {
        return {
          id: item.id.toString(),
          language: item.lang?.toLowerCase() || Languages.ORIGINAL,
        } as ITrack;
      }) || [];
    const subtitles =
      player?.subtitleTracks.map((item) => {
        return {
          id: item.id.toString(),
          language: item.lang?.toLowerCase() || Languages.ORIGINAL,
        } as ITrack;
      }) || [];
    dispatch(setTracks({ audio, subtitles }));
  }, [player]);

  // Effect hooks

  // Reset ready state when video source changes
  useEffect(() => {
    dispatch(setReady(false));
  }, [video?.src]);

  // Update audio track when audio selection changes
  useEffect(() => {
    updateAudio();
  }, [audio]);

  // Update subtitle track when subtitle selection changes
  useEffect(() => {
    updateSubtitles();
  }, [subtitles]);

  // Main effect: Set up all HLS.js player and video element event listeners
  useEffect(() => {
    const seeking = state === PlayerState.SEEK || state === PlayerState.SEEKING;

    /**
     * Handles the 'canplay' event when the player has enough data to start playing.
     * Marks player as ready and initializes tracks and playback data.
     */
    const onCanPlay = () => {
      dispatch(setReady(true));
      updateData();
      updateTracks();
    };

    /**
     * Handles the 'ended' event when playback completes.
     * Prevents multiple end events from being dispatched.
     */
    const onEnded = () => {
      if (!endedRef.current) {
        dispatch(playerEndAction());
        endedRef.current = true;
      }
    };

    /**
     * Handles HLS.js player errors.
     * Only processes fatal errors, ignoring non-fatal errors.
     * Parses error details and types to determine the appropriate error to dispatch.
     *
     * @see https://github.com/video-dev/hls.js/blob/master/src/errors.ts
     * @see https://github.com/video-dev/hls.js/blob/master/docs/API.md#errors
     */
    const onError = (event: Events.ERROR, data: ErrorData) => {
      // https://github.com/video-dev/hls.js/blob/master/src/errors.ts
      // https://github.com/video-dev/hls.js/blob/master/docs/API.md#errors
      // Readd if needed, but we are only going to manage fatal errors
      // if (
      //   [
      //     HLS_DETAIL_ERROR.AUDIO_TRACK_LOAD_ERROR,
      //     HLS_DETAIL_ERROR.AUDIO_TRACK_LOAD_TIMEOUT,
      //     HLS_DETAIL_ERROR.BUFFER_FULL_ERROR,
      //     HLS_DETAIL_ERROR.BUFFER_NUDGE_ON_STALL,
      //     HLS_DETAIL_ERROR.BUFFER_SEEK_OVER_HOLE,
      //     HLS_DETAIL_ERROR.BUFFER_STALLED_ERROR,
      //     HLS_DETAIL_ERROR.FRAG_DECRYPT_ERROR,
      //     HLS_DETAIL_ERROR.FRAG_GAP,
      //     HLS_DETAIL_ERROR.FRAG_LOAD_ERROR,
      //     HLS_DETAIL_ERROR.FRAG_LOAD_TIMEOUT,
      //     HLS_DETAIL_ERROR.FRAG_PARSING_ERROR,
      //     HLS_DETAIL_ERROR.LEVEL_EMPTY_ERROR,
      //     HLS_DETAIL_ERROR.LEVEL_LOAD_ERROR,
      //     HLS_DETAIL_ERROR.LEVEL_LOAD_TIMEOUT,
      //     HLS_DETAIL_ERROR.LEVEL_PARSING_ERROR,
      //     HLS_DETAIL_ERROR.LEVEL_SWITCH_ERROR,
      //     HLS_DETAIL_ERROR.MANIFEST_LOAD_ERROR,
      //     HLS_DETAIL_ERROR.MANIFEST_LOAD_TIMEOUT,
      //     HLS_DETAIL_ERROR.MANIFEST_PARSING_ERROR,
      //     HLS_DETAIL_ERROR.SUBTITLE_LOAD_ERROR,
      //     HLS_DETAIL_ERROR.SUBTITLE_TRACK_LOAD_TIMEOUT,
      //   ].includes(data.details as unknown as HLS_DETAIL_ERROR)
      // ) {
      //   // Excluded errors so no need to handle them
      //   return;
      // }
      if (data?.fatal !== undefined && !data.fatal) {
        // Exclude non-filtered non-fatal errors
        return;
      }
      const detail =
        PlayerUtils.getHlsjsError(data?.details) || PlayerError.UNKNOWN;
      const type =
        PlayerUtils.getHlsjsError(data?.type, data?.networkDetails) ||
        PlayerError.UNKNOWN;
      const error: IPlayerError = {
        detail,
        player: PlayerType.HLSJS,
        type,
      };
      dispatch(setError(error));
    };

    /**
     * Handles the 'loadstart' event when media loading begins.
     */
    const onLoaded = () => {
      dispatch(setState(PlayerState.LOADED));
    };

    /**
     * Handles the 'pause' event.
     * No action required as pause state is managed elsewhere.
     */
    const onPause = () => {
      // No action required
    };

    /**
     * Handles the 'play' event when playback starts.
     * No action required as play state is managed elsewhere.
     */
    const onPlay = () => {
      // No action required
    };

    /**
     * Handles the 'playing' event when playback is active.
     * Only updates state if not currently seeking.
     */
    const onPlaying = () => {
      !seeking && dispatch(setState(PlayerState.PLAYING));
    };

    /**
     * Handles the 'seek' event when seeking begins.
     */
    const onSeek = () => {
      dispatch(setState(PlayerState.SEEK));
    };

    /**
     * Handles the 'seeked' event when seeking completes.
     */
    const onSeeked = () => {
      seeking && dispatch(setState(PlayerState.SEEKED));
    };

    /**
     * Handles the 'stalled' event when playback stalls due to buffering.
     */
    const onStalled = () => {
      dispatch(setState(PlayerState.WAITING));
    };

    /**
     * Handles the 'timeupdate' event to update playback position.
     */
    const onTimeUpdate = () => {
      updateData();
    };

    /**
     * Handles the 'waiting' event when player is waiting for data.
     */
    const onWaiting = () => {
      dispatch(setState(PlayerState.WAITING));
    };

    // Register HLS.js player event listeners
    if (player) {
      player?.on('hlsError', onError);
    }
    // Register HTML5 video element event listeners
    if (video) {
      video.addEventListener('canplay', onCanPlay);
      video.addEventListener('ended', onEnded);
      video.addEventListener('loadstart', onLoaded);
      video.addEventListener('pause', onPause);
      video.addEventListener('play', onPlay);
      video.addEventListener('playing', onPlaying);
      video.addEventListener('seek', onSeek);
      video.addEventListener('seeked', onSeeked);
      video.addEventListener('stalled', onStalled);
      video.addEventListener('timeupdate', onTimeUpdate);
      video.addEventListener('waiting', onWaiting);
    }
    // Cleanup: Remove all event listeners on unmount
    return () => {
      if (player) {
        player?.off('hlsError', onError);
      }
      if (video) {
        video.removeEventListener('canplay', onCanPlay);
        video.removeEventListener('ended', onEnded);
        video.removeEventListener('loadstart', onLoaded);
        video.removeEventListener('play', onPlay);
        video.removeEventListener('playing', onPlaying);
        video.removeEventListener('seek', onSeek);
        video.removeEventListener('stalled', onStalled);
        video.removeEventListener('timeupdate', onTimeUpdate);
        video.removeEventListener('waiting', onWaiting);
      }
    };
  }, [player, video, state, userInteraction]);

  // This component doesn't render anything, it only manages event listeners
  return null;
}
